/**
 * 目录树操作工具函数
 *
 * 核心设计：每个 OutlineItem 有稳定唯一 uid（不随排序改变），
 * 显示编号 id 在增删/排序后重新计算，确保内容关联不断裂。
 */

import { OutlineItem } from '../types';

// ────────────────────── 唯一标识生成 ──────────────────────

/** 生成短唯一标识，不用额外依赖 */
export function generateUid(): string {
  return 'u' + Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
}

// ────────────────────── 数据迁移 ──────────────────────

/** 确保所有项有 uid（向后兼容：无 uid 的用 id 代替） */
export function ensureUids(items: OutlineItem[]): OutlineItem[] {
  return items.map(item => ({
    ...item,
    uid: item.uid || item.id,
    children: item.children ? ensureUids(item.children) : item.children,
  }));
}

// ────────────────────── 查找操作 ──────────────────────

/** 按 uid 递归查找节点，返回节点引用（只读查找） */
export function findByUid(items: OutlineItem[], targetUid: string): OutlineItem | null {
  for (const item of items) {
    if (item.uid === targetUid) return item;
    if (item.children) {
      const found = findByUid(item.children, targetUid);
      if (found) return found;
    }
  }
  return null;
}

/** 按 uid 查找父数组及索引，返回 { parentArr, index, parentUid } */
export function findParentInfo(
  items: OutlineItem[],
  targetUid: string,
  parentUid?: string,
): { parentArr: OutlineItem[]; index: number; parentUid: string } | null {
  for (let i = 0; i < items.length; i++) {
    if (items[i].uid === targetUid) {
      return { parentArr: items, index: i, parentUid: parentUid || '' };
    }
    if (items[i].children) {
      const result = findParentInfo(items[i].children!, targetUid, items[i].uid);
      if (result) return result;
    }
  }
  return null;
}

// ────────────────────── 重编号显示 ID ──────────────────────

/** 递归重编号所有显示 ID（不改变 uid） */
export function reorderDisplayIds(
  items: OutlineItem[],
  parentPrefix?: string,
): OutlineItem[] {
  return items.map((item, index) => {
    const newId = parentPrefix
      ? `${parentPrefix}.${index + 1}`
      : `${index + 1}`;
    return {
      ...item,
      id: newId,
      children: item.children
        ? reorderDisplayIds(item.children, newId)
        : item.children,
    };
  });
}

// ────────────────────── 增删改操作 ──────────────────────

/** 创建新目录项 */
export function createOutlineItem(title = '新目录项', description = '请编辑描述'): OutlineItem {
  return {
    uid: generateUid(),
    id: '',   // 占位，由调用方重编号
    title,
    description,
  };
}

// ──── 添加根级项 ────

export function addRootItem(items: OutlineItem[]): { items: OutlineItem[]; newItem: OutlineItem } {
  const newItem = createOutlineItem();
  const result = [...items, newItem];
  return { items: reorderDisplayIds(result), newItem };
}

// ──── 添加子项 ────

export function addChildItem(
  items: OutlineItem[],
  parentUid: string,
): { items: OutlineItem[]; newItem: OutlineItem } {
  const newItem = createOutlineItem();

  const updateChildren = (arr: OutlineItem[]): OutlineItem[] =>
    arr.map(item => {
      if (item.uid === parentUid) {
        return {
          ...item,
          children: [...(item.children || []), { ...newItem, id: '' }],
        };
      }
      if (item.children) {
        return { ...item, children: updateChildren(item.children) };
      }
      return item;
    });

  // 先注入新项，再全局重编号
  const injected = updateChildren(items);
  return { items: reorderDisplayIds(injected), newItem };
}

// ──── 在兄弟间插入（前/后） ────

export function insertSiblingItem(
  items: OutlineItem[],
  targetUid: string,
  position: 'before' | 'after',
): { items: OutlineItem[]; newItem: OutlineItem } {
  const newItem = createOutlineItem();
  const parentInfo = findParentInfo(items, targetUid);

  if (!parentInfo) return { items, newItem };

  const insertIndex = position === 'after' ? parentInfo.index + 1 : parentInfo.index;
  parentInfo.parentArr.splice(insertIndex, 0, { ...newItem, id: '' });

  // 返回新数组以触发 React 重渲染
  return { items: reorderDisplayIds([...items]), newItem };
}

// ──── 追加同级项（同级末尾） ────

export function addSiblingItem(
  items: OutlineItem[],
  targetUid: string,
): { items: OutlineItem[]; newItem: OutlineItem } {
  const newItem = createOutlineItem();
  const parentInfo = findParentInfo(items, targetUid);

  if (!parentInfo) return { items, newItem };

  parentInfo.parentArr.push({ ...newItem, id: '' });
  return { items: reorderDisplayIds([...items]), newItem };
}

// ──── 删除项（级联删除子项） ────

export function deleteItem(items: OutlineItem[], targetUid: string): OutlineItem[] {
  const filterRecursive = (arr: OutlineItem[]): OutlineItem[] => {
    const filtered = arr.filter(item => item.uid !== targetUid);
    return filtered.map(item => ({
      ...item,
      children: item.children ? filterRecursive(item.children) : item.children,
    }));
  };

  const result = filterRecursive(items);
  return reorderDisplayIds(result);
}

// ──── 移动项（上移/下移） ────

export function moveItem(
  items: OutlineItem[],
  targetUid: string,
  direction: 'up' | 'down',
): OutlineItem[] {
  const parentInfo = findParentInfo(items, targetUid);
  if (!parentInfo) return items;

  const { parentArr, index } = parentInfo;
  const targetIndex = direction === 'up' ? index - 1 : index + 1;

  // 边界检查
  if (targetIndex < 0 || targetIndex >= parentArr.length) return items;

  // 交换
  [parentArr[index], parentArr[targetIndex]] = [parentArr[targetIndex], parentArr[index]];

  return reorderDisplayIds([...items]);
}

// ──── 编辑项（ title / description ） ────

export function editItem(
  items: OutlineItem[],
  targetUid: string,
  updates: { title?: string; description?: string },
): OutlineItem[] {
  const updateRecursive = (arr: OutlineItem[]): OutlineItem[] =>
    arr.map(item => {
      if (item.uid === targetUid) {
        return { ...item, ...updates };
      }
      if (item.children) {
        return { ...item, children: updateRecursive(item.children) };
      }
      return item;
    });

  return updateRecursive(items);
}

// ────────────────────── 内容提取 ──────────────────────

/** 递归提取所有已生成的内容，key 为 uid */
export function extractContentData(items: OutlineItem[]): Record<string, { title: string; content: string }> {
  const result: Record<string, { title: string; content: string }> = {};
  const walk = (arr: OutlineItem[]) => {
    arr.forEach(item => {
      if (item.content) {
        result[item.uid] = { title: item.title, content: item.content };
      }
      if (item.children) walk(item.children);
    });
  };
  walk(items);
  return result;
}
